/**
 * Dynamically injects a JavaScript library into the page.
 *
 * @param {string} path - The path to the JavaScript library.
 * @param {Function|Object} [secondArg] - Either a callback function to execute when the script loads or an options object.
 * @param {Object} [options] - Options object.
 * @param {Function} [options.callback] - A callback function to execute when the script loads.
 * @param {boolean} [options.unique=false] - If true, the script will only be added (and therefore executed) if it hasn't been added before. Otherwise, it will always be added.
 *
 * @example
 * // Usage with callback as second parameter:
 * injectLibrary('path_to_script.js', callbackFunction);
 *
 * // Usage with options object as second parameter
 * injectLibrary('path_to_script.js', { callback: callbackFunction, unique: false });
 */
export function injectLibrary(path, secondArg = {}, options = {}) {
    let callback;
    let unique = false;

    // Check if second argument is a function (for backward compatibility)
    if (typeof secondArg === 'function') {
        callback = secondArg;
    } else if (typeof secondArg === 'object') {
        // Extract properties from the second argument when it's an options object
        ({callback, unique = false} = secondArg);
    }

    // If unique is true, and the script is already added, don't load it again. However do immediately execute the callback if provided.
    if (unique && document.querySelector(`script[src="${path}"]`)) {
        // If callback is provided, and script is already loaded, invoke the callback and exit.
        // FIXME - This is a best effort approach. If the script hasn't finished loading, this callback might execute prematurely.
        if (callback) callback();
        return;
    }

    let script = document.createElement('script');
    if (callback) script.onload = callback;
    script.type = 'text/javascript';
    script.src = path;
    document.getElementsByTagName("head")[0].appendChild(script);
}

// Dynamically add a css file
export function injectStylesheet(path, callback) {
    // If the stylesheet is already loaded, don't load it again.
    if (document.querySelector(`link[href="${path}"]`)) {
        if (callback) callback();
        return;
    }

    // Otherwise create a new stylesheet element and add it to the head.
    const element = document.createElement('link');
    element.type = 'text/css';
    element.media = 'screen';
    element.rel = 'stylesheet';
    element.href = path;
    if (callback) element.onload = callback;
    document.getElementsByTagName("head")[0].appendChild(element);
}

// http://www.tomhoppe.com/index.php/2008/03/dynamically-adding-css-through-javascript/
export function injectStyles(cssCode) {
    var styleElement = document.createElement("style");
    styleElement.type = "text/css";
    // decode html entities via a textarea
    var txt = document.createElement("textarea");
    txt.innerHTML = cssCode;
    cssCode = txt.value;
    if (styleElement.styleSheet) {
        styleElement.styleSheet.cssText = cssCode;
    } else {
        styleElement.appendChild(document.createTextNode(cssCode));
    }
    document.getElementsByTagName("head")[0].appendChild(styleElement);
}
